import type { DataDumpAggregateType, DataDumpRow } from "../../types/types";
import { useDoesCustomerShowProducing } from "utils/customHooks/genericHooks";
import { HIDE_NET_OUTPUT_VALUE } from "constants/customers";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { TopDownOrBottomUp } from "components/DataGetter/FlightConfig/FlightConfigTypes/GeneralConfigType";

export function useCleanRawDataDump() {
  const ckey = useCustomerKey();
  const [
    {
      general: { hideProducingLosses, topDownOrBottomUp },
    },
  ] = useFlightConfig();
  const doesCustomerShowProducing = useDoesCustomerShowProducing();
  const hideNetOutputForProcesses = HIDE_NET_OUTPUT_VALUE?.[ckey] ?? [];

  return (dataDump, processIds: string[]) => {
    const shouldHideNetOutput =
      processIds.length === 1 &&
      hideNetOutputForProcesses.includes(processIds[0]);
    return cleanRawDataDumpObj(
      dataDump,
      doesCustomerShowProducing,
      processIds,
      topDownOrBottomUp,
      shouldHideNetOutput,
      hideProducingLosses
    );
  };
}

export function cleanRawDataDumpObj(
  rawDataDumpObj: any,
  doesCustomerShowProducing: boolean,
  processIds: string[],
  topDownOrBottomUp: TopDownOrBottomUp,
  shouldHideNetOutput,
  hideProducingLosses
): DataDumpAggregateType {
  const {
    netOutput,
    uslePercent,
    oeePercent,
    oeePercentBottomUp,
    oeePercentTopDown,
    effectiveUptimePercent,
    effectiveProducingTime,
    effectiveProducingTimeTopDown,
    effectiveProducingTimeBottomUp,
    plannedTime,
    productionTime,
    teepPercent,
    teepPercentBottomUp,
    availabilityPercent,
    performancePercent,
    uptimePercent,
    processNames,
    producingTime,
    speedLossTime,
    minorStopTime,
    slowdownLossTime,
    slowdownLossTimeReal,
    rejectLossTime,
    waitingTime,
    unplannedTime,
    unknownTime,
    notScheduledTime,
    uncollectedTime,
    unplannedPercent,
    qualityPercent,
    taggingPercent,
    grossOutput,
    rejectOutput,
    pnlLosses,
    ticksPort1,
    ticksPort2,
    ticksPort3,
    ticksPort4,
    outputLossSum,
    productionPotentialOutput,
    totalPotentialOutput,
    operationalAvailabilityPercent,
    efficiencyPercent,
    pureEquipmentUptimePercent,
    uptimePerformancePercent,
    producingEfficiencyPercent,
    ge,
    lt,
    lastSegment,
    meanTimeBetweenFailures,
    meanTimeToRecovery,
    avgCycleTime,
    netTicks,
    grossTicks,
    rejectTicks,
    customMetric01Value,
    customMetric02Value,
    customMetric03Value,
    customMetric04Value,
    customMetric05Value,
    customMetric06Value,
    customMetric07Value,
    customMetric08Value,
    untaggedPercent,
    notScheduledPercent,
    plannedDowntimeEfficiency,
  } = rawDataDumpObj;

  const pnlLossesWithoutNegativeValues =
    pnlLosses?.unknown && pnlLosses?.quality
      ? {
          ...pnlLosses,
          unknown: doesCustomerShowProducing
            ? convertObjectNegativeValuesToZero(pnlLosses.unknown)
            : pnlLosses.unknown,
          quality: convertObjectNegativeValuesToZero(pnlLosses.quality),
        }
      : pnlLosses;

  const bottomUpDenominator = productionTime - plannedTime;

  const effectiveUptimePercentDisplay =
    // use effectiveUptimePercent if we have it
    effectiveUptimePercent ||
    // otherwise, try to calculate it manually
    bottomUpDenominator > 0
      ? (100 * effectiveProducingTimeBottomUp) / bottomUpDenominator
      : null;

  return {
    processIds,
    range: { ge: Number(ge), lt: Number(lt) },
    netOutput: shouldHideNetOutput ? undefined : netOutput, // undefined shows as '--' in components
    grossOutput,
    uslePercent,
    oeePercent:
      oeePercent ||
      (topDownOrBottomUp === "bottom-up"
        ? oeePercentBottomUp
        : topDownOrBottomUp === "top-down"
          ? oeePercentTopDown
          : oeePercentBottomUp || oeePercentTopDown),
    effectiveUptimePercent: effectiveUptimePercentDisplay,
    teepPercent,
    teepPercentBottomUp,
    availabilityPercent,
    performancePercent,
    productionTime,
    producingTime: hideProducingLosses
      ? producingTime
      : topDownOrBottomUp === "bottom-up"
        ? effectiveProducingTimeBottomUp
        : topDownOrBottomUp === "top-down"
          ? effectiveProducingTimeTopDown
          : producingTime,
    notScheduledTime,
    uptimePercent,
    ticksPort1,
    ticksPort2,
    ticksPort3,
    ticksPort4,
    outputLossSum,
    productionPotentialOutput,
    totalPotentialOutput,
    operationalAvailabilityPercent,
    efficiencyPercent,
    pureEquipmentUptimePercent,
    uptimePerformancePercent,
    producingEfficiencyPercent,
    // pnl losses data
    processNames,
    speedLossTime,
    minorStopTime,
    slowdownLossTime,
    slowdownLossTimeReal,
    rejectLossTime,
    plannedTime,
    waitingTime,
    unplannedTime,
    unknownTime,
    uncollectedTime,
    unplannedPercent,
    qualityPercent,
    taggingPercent,
    effectiveProducingTime,
    effectiveProducingTimeTopDown,
    effectiveProducingTimeBottomUp,
    rejectOutput,
    lastSegment: lastSegment && cleanRawDataDumpRow(lastSegment),
    pnlLosses: doesCustomerShowProducing
      ? pnlLosses
      : pnlLossesWithoutNegativeValues, // negative values only make sense when shown with producing
    meanTimeBetweenFailures,
    meanTimeToRecovery,
    avgCycleTime,
    netTicks,
    grossTicks,
    rejectTicks,
    customMetric01Value,
    customMetric02Value,
    customMetric03Value,
    customMetric04Value,
    customMetric05Value,
    customMetric06Value,
    customMetric07Value,
    customMetric08Value,
    untaggedPercent,
    notScheduledPercent,
    plannedDowntimeEfficiency,
  };
}

function convertObjectNegativeValuesToZero(
  object: Record<string, unknown>
): Record<string, unknown> {
  const keys = Object.keys(object);

  keys.forEach(key => {
    const value = object[key];
    if (typeof value === "number") {
      object = { ...object, [key]: value < 0 ? 0 : value };
    }
  });

  return object;
}

export function cleanRawDataDumpRow(dataDumpRow: any): DataDumpRow {
  return {
    ...dataDumpRow,
    ge: Number(dataDumpRow.ge),
    lt: Number(dataDumpRow.lt),
    messageTime: Number(dataDumpRow.asOf),
    skuStart: Number(dataDumpRow.skuStart),
    callStart: dataDumpRow.callStart && Number(dataDumpRow.callStart),
  };
}
